import React from 'react';
import Box from '@mui/material/Box';
import {useLocation, useNavigate} from 'react-router';
import type {FCWithChildren} from 'types';
import type {LoadStatus} from 'api/types';
import {useLocalization} from 'localization';
import {px2rem} from 'theme';
import {ActivityIndicator} from 'components/activityIndicator';
import {ActionButton} from 'components/button';
import {Card} from 'components/card';
import {Layout} from 'components/layout';
import {Spacer} from 'components/spacer';
import {Text} from 'components/text';
import {useLoadingText} from './hooks';

const SceneContentName = 'SceneContentName';

type Props = {
  loadStatus?: LoadStatus;
  error?: string;
  loadingType?: string;
};

const SceneContent: FCWithChildren<Props> = ({
  loadStatus,
  error,
  loadingType,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useLocalization();
  const loadingText = useLoadingText(loadingType);

  if (loadStatus === 'NotLoaded' || loadStatus === 'Loading')
    return (
      <Layout adjustCenter paddingTop="10vh">
        <ActivityIndicator />
        <Text variant="h4" sx={{mt: 2}}>
          {loadingText}
        </Text>
      </Layout>
    );

  if (loadStatus === 'Failed') {
    return (
      <Layout adjustCenter paddingTop="10vh">
        <Layout sx={{maxWidth: px2rem(400)}}>
          <Card color="secondary">
            <Layout p={2} adjustCenter>
              <Text variant="h3" kind="error">
                {error ?? t('error.failedToLoadPage')}
              </Text>
              <Spacer size={4} />
              <Layout sx={{minWidth: px2rem(100)}}>
                <ActionButton
                  title={t('error.retry')}
                  onClick={() =>
                    navigate(`${location.pathname}${location.search}`)
                  }
                />
              </Layout>
            </Layout>
          </Card>
        </Layout>
      </Layout>
    );
  }

  return <Box id="scene-content">{children}</Box>;
};

SceneContent.displayName = SceneContentName;

export {SceneContent, SceneContentName};
